/**
 * lx-empty-state —— 通用空态
 *
 * 首次启动时数据库为空（D2），三个页面都会用到空态引导。
 * 空态不只是「没有内容」，而是明确告诉用户下一步能做什么 ——
 * 尤其是「直接对下面的框说话」这条提示，它把 AI 入口的价值说清楚了。
 */

import { LxElement, define } from '@/core/base-element';

export interface EmptyStateOptions {
  icon?: string;
  title: string;
  description?: string;
  /** 主行动按钮文案 */
  actionLabel?: string;
  /** 提示用户可以说的话 */
  hint?: string;
}

/** 空态图标：一个对话气泡轮廓 */
const DEFAULT_ICON = `
  <path d="M4 7.5A3.5 3.5 0 0 1 7.5 4h9A3.5 3.5 0 0 1 20 7.5v6a3.5 3.5 0 0 1-3.5 3.5H10l-4.4 3.3A.6.6 0 0 1 4.7 20v-2.6A3.5 3.5 0 0 1 4 15.5v-8Z"
        stroke-linecap="round" stroke-linejoin="round"/>
`;

export class LxEmptyState extends LxElement {
  private _options: EmptyStateOptions = { title: '' };

  /** 供父组件以属性方式配置 */
  set options(value: EmptyStateOptions) {
    this._options = value;
    this.update();
  }

  get options(): EmptyStateOptions {
    return this._options;
  }

  protected styles(): string {
    return `
      /* 高度完全由宿主的 flex 分配决定。
         刻意不写 min-height:100% —— 百分比高度在「宿主是 flex 子项」时
         没有确定参照，Chrome 会退化用内容高度，从而把外层容器撑高、
         凭空产生滚动条。这里只声明「我是 flex 容器 + 内容居中」即可。 */
      :host {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        min-height: 0;
        padding: var(--sp-5) var(--sp-5);
      }

      /* 纵向空间不够时优先压缩留白，而不是把文字挤出去 */
      @media (max-height: 640px) {
        :host {
          padding: var(--sp-4) var(--sp-5);
        }

        .icon {
          width: 44px;
          height: 44px;
          margin-bottom: 0;
        }
      }

      .wrap {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        gap: var(--sp-3);
        max-width: 300px;
      }

      .icon {
        width: 56px;
        height: 56px;
        margin-bottom: var(--sp-1);
        color: var(--c-fg-subtle);
      }

      .icon svg {
        width: 100%;
        height: 100%;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.4;
      }

      .title {
        font-size: var(--fs-lg);
        font-weight: var(--fw-medium);
        color: var(--c-fg);
        line-height: var(--lh-tight);
      }

      .desc {
        font-size: var(--fs-sm);
        color: var(--c-fg-muted);
        line-height: var(--lh-base);
      }

      .action {
        margin-top: var(--sp-2);
        padding: var(--sp-2) var(--sp-5);
        border-radius: var(--radius-full);
        background: var(--c-primary);
        color: var(--c-primary-fg);
        font-size: var(--fs-sm);
        font-weight: var(--fw-medium);
        transition: opacity var(--dur-fast) var(--ease-standard);
      }

      .action:hover {
        opacity: 0.88;
      }

      .action:active {
        opacity: 0.72;
      }

      /* 提示用户可以对 AI 说的话，用虚线框和等宽字体突出「这是一条指令」 */
      .hint {
        margin-top: var(--sp-2);
        padding: var(--sp-2) var(--sp-3);
        border: 1px dashed var(--c-border-strong);
        border-radius: var(--radius-md);
        font-size: var(--fs-xs);
        color: var(--c-fg-muted);
        line-height: var(--lh-base);
      }

      .hint code {
        font-family: var(--font-mono);
        color: var(--c-fg);
      }
    `;
  }

  protected render(): string {
    const o = this._options;
    return `
      <div class="wrap">
        <div class="icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">${o.icon ?? DEFAULT_ICON}</svg>
        </div>
        <p class="title">${this.esc(o.title)}</p>
        ${o.description ? `<p class="desc">${this.esc(o.description)}</p>` : ''}
        ${o.actionLabel ? `<button class="action" type="button" data-action>${this.esc(o.actionLabel)}</button>` : ''}
        ${o.hint ? `<p class="hint">或者对下面的输入框说：<br><code>${this.esc(o.hint)}</code></p>` : ''}
      </div>
    `;
  }

  protected onMounted(): void {
    this.$('[data-action]')?.addEventListener('click', () => {
      this.dispatchEvent(
        new CustomEvent('empty-action', { bubbles: true, composed: true }),
      );
    });
  }
}

define('lx-empty-state', LxEmptyState);
